import { useState, useEffect } from "react";
import Card from "@/components/Card";
import Button from "@/components/Button";
import {
  Plus,
  Edit2,
  Trash2,
  CreditCard,
  Building2,
  Users,
  Loader2,
  MessageCircle,
  AlertTriangle,
  CalendarClock,
  Eye,
  EyeOff,
  LayoutGrid,
  PhoneCall,
  Mail,
  Package,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";
import { useConfirm } from "../../contexts/ConfirmContext";
import { extractApiErrorMessage } from "../../lib/api-errors";

interface PlanFeatures {
  whatsappNotifications?: boolean;
  emailReminders?: boolean;
  analytics?: boolean;
  widgetCustomization?: boolean;
  maxReservations?: number;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: string;
  features: PlanFeatures;
  maxLocations: number;
  maxUsers: number;
  hasAppointments: boolean;
  hasTableMaps: boolean;
  hasWhatsApp: boolean;
  hasVoiceAi: boolean;
  hasEmailCampaigns: boolean;
  isActive: boolean;
  trialDays: number;
  createdAt: string;
  _count: {
    organizations: number;
  };
}

// -----------------------------------------------------------------------------
// Add-ons — catálogo de complementos (contrato: reservamanager-backend
// src/modules/admin/controller.ts getAllAddOns/createAddOn/updateAddOn/deleteAddOn,
// montado en /api/admin/add-ons). Verificado antes de escribir esta sección:
// GET/POST/PUT/DELETE existen los 4, el sync con Stripe es automático en
// create/update (solo si isActive && price > 0) y un PUT sin cambios ({})
// re-lanza el sync — por eso "Reintentar sincronización" es un PUT vacío.
// -----------------------------------------------------------------------------

// Mismo set de flags que PLAN_FEATURE_FLAGS en el controller del backend
// (admin/controller.ts) — si el backend añade uno nuevo hay que replicarlo
// aquí también.
type PlanFeatureFlag =
  | "hasAppointments"
  | "hasTableMaps"
  | "hasWhatsApp"
  | "hasVoiceAi"
  | "hasEmailCampaigns";

const PLAN_FEATURE_OPTIONS: { value: PlanFeatureFlag; label: string }[] = [
  { value: "hasTableMaps", label: "Mapa de mesas / agenda" },
  { value: "hasWhatsApp", label: "Notificaciones por WhatsApp" },
  { value: "hasVoiceAi", label: "Centralita IA por voz" },
  { value: "hasEmailCampaigns", label: "Campañas de email con IA" },
  { value: "hasAppointments", label: "Gestión de citas" },
];

function planFeatureLabel(feature: string | null): string | null {
  if (!feature) return null;
  return PLAN_FEATURE_OPTIONS.find((f) => f.value === feature)?.label ?? feature;
}

interface StripeSyncResult {
  success: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  priceChanged?: boolean;
  error?: string;
}

interface AddOn {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  interval: string;
  isActive: boolean;
  requiredPlanFeature: string | null;
  stripeProductId: string | null;
  stripePriceId: string | null;
  createdAt: string;
  updatedAt: string;
  activeSubscriptions: number;
  _count: { subscriptions: number };
}

interface AddOnFormData {
  code: string;
  name: string;
  description: string;
  price: number | "";
  interval: "monthly" | "yearly";
  isActive: boolean;
  requiredPlanFeature: string; // "" = Ninguna
}

const EMPTY_ADDON_FORM: AddOnFormData = {
  code: "",
  name: "",
  description: "",
  price: "",
  interval: "monthly",
  isActive: true,
  requiredPlanFeature: "",
};

// Un add-on solo puede sincronizarse con Stripe si está activo y tiene
// precio > 0 (misma condición que el controller: `isActive && price > 0`).
// Si no se cumple, "Reintentar sincronización" sería un PUT en vano.
function canRetrySync(addOn: AddOn): boolean {
  return addOn.isActive && addOn.price > 0;
}

// Construye el toast a partir del resultado REAL del sync con Stripe que
// devuelve la API (campo `stripe` de create/update) — nunca un éxito
// genérico si Stripe falló. `stripe === null` significa que el backend ni
// lo intentó (add-on inactivo, precio 0, o Stripe sin configurar en el
// entorno); `stripe.success === false` significa que lo intentó y falló.
function buildStripeToast(
  baseMessage: string,
  stripe: StripeSyncResult | null | undefined,
): { message: string; type: "success" | "error" | "info" } {
  if (!stripe) {
    return {
      message: `${baseMessage}. Sin sincronizar con Stripe (revisa que el add-on esté activo y tenga un precio mayor que 0€).`,
      type: "info",
    };
  }
  if (stripe.success) {
    return {
      message: `${baseMessage} y sincronizado con Stripe${stripe.priceChanged ? " (precio actualizado)" : ""}.`,
      type: "success",
    };
  }
  return {
    message: `${baseMessage}, pero Stripe no sincronizó: ${stripe.error || "motivo desconocido"}.`,
    type: "error",
  };
}

export default function Plans() {
  const [activeSection, setActiveSection] = useState<"plans" | "addons">(
    "plans",
  );
  const { showToast } = useGlobalToast();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: 0,
    interval: "monthly",
    maxLocations: 1,
    maxUsers: 5,
    hasAppointments: false,
    hasTableMaps: false,
    hasWhatsApp: false,
    hasVoiceAi: false,
    hasEmailCampaigns: false,
    isActive: true,
    trialDays: 5,
    features: {
      whatsappNotifications: false,
      emailReminders: true,
      analytics: false,
      widgetCustomization: false,
      maxReservations: -1, // -1 = ilimitadas
    } as PlanFeatures,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get("/admin/plans");
      setPlans(response.data.data);
    } catch (err) {
      showToast("Error al cargar planes", "error");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPlan) {
        await apiClient.put(`/admin/plans/${editingPlan.id}`, formData);
      } else {
        await apiClient.post("/admin/plans", formData);
      }
      setShowModal(false);
      setEditingPlan(null);
      resetForm();
      fetchPlans();
    } catch (err) {
      console.error(err);
    }
  };

  const openEditModal = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || "",
      price: plan.price,
      interval: plan.interval,
      maxLocations: plan.maxLocations,
      maxUsers: plan.maxUsers,
      hasAppointments: plan.hasAppointments ?? false,
      hasTableMaps: plan.hasTableMaps ?? false,
      hasWhatsApp: plan.hasWhatsApp ?? false,
      hasVoiceAi: plan.hasVoiceAi ?? false,
      hasEmailCampaigns: plan.hasEmailCampaigns ?? false,
      isActive: plan.isActive,
      trialDays: plan.trialDays ?? 5,
      features: {
        whatsappNotifications: plan.features?.whatsappNotifications || false,
        emailReminders: plan.features?.emailReminders !== false,
        analytics: plan.features?.analytics || false,
        widgetCustomization: plan.features?.widgetCustomization || false,
        maxReservations: plan.features?.maxReservations ?? -1,
      },
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: 0,
      interval: "monthly",
      maxLocations: 1,
      maxUsers: 5,
      hasAppointments: false,
      hasTableMaps: false,
      hasWhatsApp: false,
      hasVoiceAi: false,
      hasEmailCampaigns: false,
      isActive: true,
      trialDays: 5,
      features: {
        whatsappNotifications: false,
        emailReminders: true,
        analytics: false,
        widgetCustomization: false,
        maxReservations: -1,
      },
    });
  };

  const handleToggleActive = async (plan: Plan) => {
    try {
      await apiClient.put(`/admin/plans/${plan.id}`, {
        isActive: !plan.isActive,
      });
      showToast(
        plan.isActive
          ? `Plan "${plan.name}" ocultado a los clientes`
          : `Plan "${plan.name}" visible para los clientes`,
        "success",
      );
      fetchPlans();
    } catch (err) {
      const errorMessage =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cambiar la visibilidad del plan";
      showToast(errorMessage || "Error al cambiar la visibilidad", "error");
      console.error(err);
    }
  };

  const openDeleteModal = (plan: Plan) => {
    setDeletingPlan(plan);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;

    try {
      setIsDeleting(true);
      await apiClient.delete(`/admin/plans/${deletingPlan.id}`);
      showToast(
        `Plan "${deletingPlan.name}" eliminado correctamente`,
        "success",
      );
      setShowDeleteModal(false);
      setDeletingPlan(null);
      fetchPlans();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al eliminar el plan";
      showToast(errorMessage || "Error al eliminar el plan", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Pestañas de sección: Planes / Add-ons */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4 md:gap-6 -mb-px overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveSection("plans")}
            className={`py-3 text-sm font-medium border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${
              activeSection === "plans"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <CreditCard className="h-4 w-4" />
            Planes
          </button>
          <button
            type="button"
            onClick={() => setActiveSection("addons")}
            className={`py-3 text-sm font-medium border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${
              activeSection === "addons"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Package className="h-4 w-4" />
            Add-ons
          </button>
        </nav>
      </div>

      {activeSection === "plans" &&
        (isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  Planes de Suscripción
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Gestiona los planes y precios de la plataforma
                </p>
              </div>
              <Button onClick={openCreateModal}>
                <Plus size={20} className="inline mr-2" />
                Nuevo Plan
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <Card key={plan.id}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-primary-100 rounded-lg mr-3">
                        <CreditCard className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {plan.name}
                        </h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${plan.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                        >
                          {plan.isActive ? "Visible" : "Oculto"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleActive(plan)}
                        className={`p-1.5 rounded ${
                          plan.isActive
                            ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        }`}
                        title={
                          plan.isActive
                            ? "Visible para clientes — clic para ocultar"
                            : "Oculto — clic para mostrar a clientes"
                        }
                      >
                        {plan.isActive ? (
                          <Eye size={16} />
                        ) : (
                          <EyeOff size={16} />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(plan)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Editar plan"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => openDeleteModal(plan)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Eliminar plan"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">
                      {plan.price}€
                    </span>
                    <span className="text-gray-500">
                      /{plan.interval === "monthly" ? "mes" : "año"}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    {plan.description || "Sin descripción"}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Building2 className="h-4 w-4 mr-2" />
                      <span>
                        {plan.maxLocations} restaurante
                        {plan.maxLocations !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2" />
                      <span>
                        {plan.maxUsers} usuario{plan.maxUsers !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {plan.hasTableMaps && (
                      <div className="flex items-center text-sm text-gray-600">
                        <LayoutGrid className="h-4 w-4 mr-2" />
                        <span>Mapa de mesas / agenda</span>
                      </div>
                    )}
                    {plan.hasWhatsApp && (
                      <div className="flex items-center text-sm text-green-600">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        <span>WhatsApp incluido</span>
                      </div>
                    )}
                    {plan.hasVoiceAi && (
                      <div className="flex items-center text-sm text-purple-600">
                        <PhoneCall className="h-4 w-4 mr-2" />
                        <span>Centralita IA (add-on disponible)</span>
                      </div>
                    )}
                    {plan.hasEmailCampaigns && (
                      <div className="flex items-center text-sm text-blue-600">
                        <Mail className="h-4 w-4 mr-2" />
                        <span>Campañas de email IA</span>
                      </div>
                    )}
                    {plan.hasAppointments && (
                      <div className="flex items-center text-sm text-purple-600">
                        <CalendarClock className="h-4 w-4 mr-2" />
                        <span>Gestión de citas</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      {plan._count.organizations} organización
                      {plan._count.organizations !== 1 ? "es" : ""} suscritas
                    </p>
                  </div>
                </Card>
              ))}

              {plans.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No hay planes creados. Crea el primero.
                </div>
              )}
            </div>

            {/* Modal */}
            {showModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90dvh] flex flex-col">
                  <h2 className="text-xl font-semibold text-gray-900 px-6 py-4 border-b border-gray-100 shrink-0">
                    {editingPlan ? "Editar Plan" : "Nuevo Plan"}
                  </h2>

                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col min-h-0 flex-1"
                  >
                    <div className="flex-1 min-h-0 space-y-4 overflow-y-auto px-6 py-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ej: Pro"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Descripción
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              description: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={2}
                          placeholder="Descripción del plan..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Precio (€)
                          </label>
                          <input
                            type="number"
                            required
                            min="0"
                            step="0.01"
                            value={formData.price}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                price: parseFloat(e.target.value),
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Intervalo
                          </label>
                          <select
                            value={formData.interval}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                interval: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="monthly">Mensual</option>
                            <option value="yearly">Anual</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Máx. Restaurantes
                          </label>
                          <input
                            type="number"
                            required
                            min="1"
                            value={formData.maxLocations}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                maxLocations: parseInt(e.target.value),
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Máx. Usuarios
                          </label>
                          <input
                            type="number"
                            required
                            min="1"
                            value={formData.maxUsers}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                maxUsers: parseInt(e.target.value),
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Días de prueba gratuita
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="90"
                          value={formData.trialDays}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              trialDays: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Días con todas las funciones premium desbloqueadas.
                          El trial de los nuevos registros se toma del plan{" "}
                          <strong>Free</strong> (todos empiezan ahí). Pon 0
                          para no ofrecer prueba.
                        </p>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              isActive: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="isActive"
                          className="ml-2 text-sm text-gray-700"
                        >
                          Plan visible para nuevos clientes
                        </label>
                      </div>

                      <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <input
                          type="checkbox"
                          id="hasAppointments"
                          checked={formData.hasAppointments}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              hasAppointments: e.target.checked,
                            })
                          }
                          className="h-4 w-4 mt-0.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="hasAppointments"
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          <span className="font-medium flex items-center gap-1.5">
                            <CalendarClock className="h-4 w-4 text-purple-600" />
                            Permite gestión de citas
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Desbloquea el modo “citas” para peluquerías,
                            clínicas, talleres y otros negocios sin mesa.
                          </span>
                        </label>
                      </div>

                      {/* Funciones premium reales (flags que desbloquean acceso) */}
                      <div className="border-t border-gray-200 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Funciones premium del plan
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Lo que el plan desbloquea para el cliente. Ojo: la
                          Centralita IA no se incluye gratis — esta casilla
                          solo permite{" "}
                          <strong>contratarla como add-on aparte</strong>.
                        </p>
                        <div className="space-y-3">
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.hasTableMaps}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  hasTableMaps: e.target.checked,
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <LayoutGrid className="h-4 w-4 text-gray-600" />
                            Mapa de mesas / agenda de especialistas
                          </label>

                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.hasWhatsApp}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  hasWhatsApp: e.target.checked,
                                })
                              }
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <MessageCircle className="h-4 w-4 text-green-600" />
                            Notificaciones por WhatsApp
                          </label>

                          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.hasVoiceAi}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  hasVoiceAi: e.target.checked,
                                })
                              }
                              className="h-4 w-4 mt-0.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <PhoneCall className="h-4 w-4 mt-0.5 text-purple-600" />
                            <span>
                              Centralita IA por voz
                              <span className="block text-xs text-gray-500">
                                Permite contratar el add-on de la centralita
                                en este plan.
                              </span>
                            </span>
                          </label>

                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.hasEmailCampaigns}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  hasEmailCampaigns: e.target.checked,
                                })
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <Mail className="h-4 w-4 text-blue-600" />
                            Campañas de email con IA
                          </label>
                        </div>
                      </div>

                      {/* Features del Plan */}
                      <div className="border-t border-gray-200 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Ajustes adicionales
                        </label>
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="emailReminders"
                              checked={
                                formData.features.emailReminders !== false
                              }
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  features: {
                                    ...formData.features,
                                    emailReminders: e.target.checked,
                                  },
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor="emailReminders"
                              className="ml-2 text-sm text-gray-700"
                            >
                              Recordatorios por email
                            </label>
                          </div>

                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="analytics"
                              checked={formData.features.analytics || false}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  features: {
                                    ...formData.features,
                                    analytics: e.target.checked,
                                  },
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor="analytics"
                              className="ml-2 text-sm text-gray-700"
                            >
                              Analytics avanzados
                            </label>
                          </div>

                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="widgetCustomization"
                              checked={
                                formData.features.widgetCustomization ||
                                false
                              }
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  features: {
                                    ...formData.features,
                                    widgetCustomization: e.target.checked,
                                  },
                                })
                              }
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor="widgetCustomization"
                              className="ml-2 text-sm text-gray-700"
                            >
                              Personalización del widget
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3 px-6 py-4 border-t border-gray-100 shrink-0">
                      <Button
                        type="button"
                        variant="secondary"
                        className="flex-1"
                        onClick={() => setShowModal(false)}
                      >
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1">
                        {editingPlan ? "Guardar" : "Crear"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal de confirmación de eliminación */}
            {showDeleteModal && deletingPlan && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-100 rounded-full">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Eliminar Plan
                    </h2>
                  </div>

                  <p className="text-gray-600 mb-4">
                    ¿Estás seguro de que quieres eliminar el plan{" "}
                    <strong>"{deletingPlan.name}"</strong>?
                  </p>

                  {deletingPlan._count.organizations > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Atención:</strong> Este plan tiene{" "}
                        {deletingPlan._count.organizations} organización(es)
                        suscrita(s). No podrás eliminarlo hasta que cambies
                        esas organizaciones a otro plan.
                      </p>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      variant="secondary"
                      className="flex-1"
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingPlan(null);
                      }}
                      disabled={isDeleting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      className="flex-1"
                      onClick={handleDelete}
                      disabled={
                        isDeleting || deletingPlan._count.organizations > 0
                      }
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Eliminando...
                        </>
                      ) : (
                        "Eliminar"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        ))}

      {activeSection === "addons" && <AddOnsSection />}
    </div>
  );
}

// =============================================================================
// Sección Add-ons
// =============================================================================

function AddOnsSection() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingAddOn, setEditingAddOn] = useState<AddOn | null>(null);
  const [formData, setFormData] = useState<AddOnFormData>(EMPTY_ADDON_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const [deletingAddOn, setDeletingAddOn] = useState<AddOn | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAddOns();
  }, []);

  const fetchAddOns = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const response = await apiClient.get("/admin/add-ons");
      setAddOns(response.data.data);
    } catch (err) {
      setLoadError(extractApiErrorMessage(err, "Error al cargar los add-ons"));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAddOn(null);
    setFormData(EMPTY_ADDON_FORM);
    setShowModal(true);
  };

  const openEditModal = (addOn: AddOn) => {
    setEditingAddOn(addOn);
    setFormData({
      code: addOn.code,
      name: addOn.name,
      description: addOn.description || "",
      price: addOn.price,
      interval: addOn.interval === "yearly" ? "yearly" : "monthly",
      isActive: addOn.isActive,
      requiredPlanFeature: addOn.requiredPlanFeature || "",
    });
    setShowModal(true);
  };

  // Permite escribir el precio con hasta 2 decimales (coma o punto), mismo
  // regex de validación de dinero que Services.tsx/Menus.tsx
  // (`/^\d+(\.\d{1,2})?$/`), pero guardado ya como number|"" (no como
  // string) para no reparsear en el submit. Si el usuario deja el punto a
  // medio escribir ("12.") no actualizamos el estado: sin setState no hay
  // re-render, así el input no "salta" ni borra lo que se acaba de teclear.
  const handlePriceChange = (raw: string) => {
    if (raw.trim() === "") {
      setFormData((prev) => ({ ...prev, price: "" }));
      return;
    }
    const normalized = raw.replace(",", ".");
    if (!/^\d*(\.\d{0,2})?$/.test(normalized)) return;
    if (normalized === "." || /\.$/.test(normalized)) return;
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setFormData((prev) => ({ ...prev, price: parsed }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast("El nombre es obligatorio", "error");
      return;
    }
    if (!editingAddOn && !formData.code.trim()) {
      showToast("El código es obligatorio", "error");
      return;
    }
    if (formData.price === "") {
      showToast("El precio es obligatorio", "error");
      return;
    }

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      price: formData.price,
      interval: formData.interval,
      isActive: formData.isActive,
      requiredPlanFeature: formData.requiredPlanFeature || null,
    };
    if (!editingAddOn) {
      payload.code = formData.code.trim();
    }

    try {
      setIsSubmitting(true);
      let stripe: StripeSyncResult | null = null;
      if (editingAddOn) {
        const response = await apiClient.put(
          `/admin/add-ons/${editingAddOn.id}`,
          payload,
        );
        stripe = response.data.stripe;
      } else {
        const response = await apiClient.post("/admin/add-ons", payload);
        stripe = response.data.stripe;
      }
      const toast = buildStripeToast(
        editingAddOn ? "Add-on guardado" : "Add-on creado",
        stripe,
      );
      showToast(toast.message, toast.type);
      setShowModal(false);
      setEditingAddOn(null);
      setFormData(EMPTY_ADDON_FORM);
      fetchAddOns();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "Error al guardar el add-on"),
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (addOn: AddOn) => {
    if (addOn.isActive) {
      const ok = await confirm({
        title: `¿Desactivar "${addOn.name}"?`,
        message:
          "Los clientes con este add-on ya contratado no se ven afectados; solo deja de ofrecerse para nuevas contrataciones.",
        confirmText: "Desactivar",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      setTogglingId(addOn.id);
      const response = await apiClient.put(`/admin/add-ons/${addOn.id}`, {
        isActive: !addOn.isActive,
      });
      const toast = buildStripeToast(
        addOn.isActive ? "Add-on desactivado" : "Add-on activado",
        response.data.stripe,
      );
      showToast(toast.message, toast.type);
      fetchAddOns();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "Error al cambiar el estado del add-on"),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  // PUT sin cambios en el body: el controller re-lanza syncAddOnToStripe con
  // los valores ya persistidos (verificado en admin/controller.ts).
  const handleRetrySync = async (addOn: AddOn) => {
    try {
      setRetryingId(addOn.id);
      const response = await apiClient.put(`/admin/add-ons/${addOn.id}`, {});
      const toast = buildStripeToast(
        "Reintento de sincronización",
        response.data.stripe,
      );
      showToast(toast.message, toast.type);
      fetchAddOns();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "Error al reintentar la sincronización"),
        "error",
      );
    } finally {
      setRetryingId(null);
    }
  };

  const openDeleteModal = (addOn: AddOn) => {
    setDeletingAddOn(addOn);
    setDeleteConfirmText("");
  };

  const handleDelete = async () => {
    if (!deletingAddOn) return;
    try {
      setIsDeleting(true);
      await apiClient.delete(`/admin/add-ons/${deletingAddOn.id}`);
      showToast(`Add-on "${deletingAddOn.name}" eliminado`, "success");
      setDeletingAddOn(null);
      setDeleteConfirmText("");
      fetchAddOns();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "Error al eliminar el add-on"),
        "error",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Catálogo de Add-ons
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Complementos que los clientes pueden contratar aparte del plan
            (ej. Centralita IA)
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus size={20} className="inline mr-2" />
          Nuevo add-on
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loadError ? (
          <div className="text-center py-16 md:py-20 px-4">
            <AlertTriangle className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 text-red-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No se pudieron cargar los add-ons
            </h3>
            <p className="text-gray-500 text-sm mb-4">{loadError}</p>
            <Button variant="outline" onClick={fetchAddOns}>
              Reintentar
            </Button>
          </div>
        ) : addOns.length === 0 ? (
          <div className="text-center py-16 md:py-20">
            <Package className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No hay add-ons creados
            </h3>
            <p className="text-gray-500 text-sm">
              Crea el primero para poder ofrecerlo a los clientes
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {addOns.map((addOn) => (
                <AddOnCard
                  key={addOn.id}
                  addOn={addOn}
                  togglingId={togglingId}
                  retryingId={retryingId}
                  onEdit={openEditModal}
                  onToggle={handleToggleActive}
                  onRetrySync={handleRetrySync}
                  onDelete={openDeleteModal}
                />
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Add-on
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Precio
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Estado
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Stripe
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">
                      Suscripciones
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {addOns.map((addOn) => (
                    <AddOnRow
                      key={addOn.id}
                      addOn={addOn}
                      togglingId={togglingId}
                      retryingId={retryingId}
                      onEdit={openEditModal}
                      onToggle={handleToggleActive}
                      onRetrySync={handleRetrySync}
                      onDelete={openDeleteModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal crear/editar */}
      {showModal && (
        <AddOnFormModal
          editingAddOn={editingAddOn}
          formData={formData}
          setFormData={setFormData}
          onPriceChange={handlePriceChange}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Modal eliminar — doble confirmación: solo add-ons sin stripePriceId
          llegan aquí (botón oculto si ya tienen Stripe), y además hay que
          escribir el código exacto para habilitar el botón. */}
      {deletingAddOn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Eliminar add-on definitivamente
              </h2>
            </div>
            <p className="text-gray-600 mb-3">
              Vas a eliminar <strong>"{deletingAddOn.name}"</strong> (
              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                {deletingAddOn.code}
              </code>
              ). Esta acción no se puede deshacer.
            </p>

            {deletingAddOn.activeSubscriptions > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Atención:</strong>{" "}
                  {deletingAddOn.activeSubscriptions} organización(es) tienen
                  este add-on activo. No podrás eliminarlo hasta que lo
                  cancelen o lo revoques.
                </p>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Escribe{" "}
              <code className="bg-gray-100 px-1 py-0.5 rounded">
                {deletingAddOn.code}
              </code>{" "}
              para confirmar
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              placeholder={deletingAddOn.code}
              autoComplete="off"
            />

            <div className="flex space-x-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setDeletingAddOn(null);
                  setDeleteConfirmText("");
                }}
                disabled={isDeleting}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="danger"
                className="flex-1"
                onClick={handleDelete}
                disabled={
                  isDeleting ||
                  deleteConfirmText !== deletingAddOn.code ||
                  deletingAddOn.activeSubscriptions > 0
                }
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                    Eliminando...
                  </>
                ) : (
                  "Eliminar definitivamente"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StripeSyncBadge({ addOn }: { addOn: AddOn }) {
  if (addOn.stripePriceId) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 w-fit">
        <CheckCircle2 className="h-3 w-3" />
        Sincronizado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 w-fit">
      <AlertTriangle className="h-3 w-3" />
      Sin sincronizar con Stripe
    </span>
  );
}

interface AddOnActionsProps {
  addOn: AddOn;
  togglingId: string | null;
  retryingId: string | null;
  onEdit: (addOn: AddOn) => void;
  onToggle: (addOn: AddOn) => void;
  onRetrySync: (addOn: AddOn) => void;
  onDelete: (addOn: AddOn) => void;
}

function AddOnActionButtons({
  addOn,
  togglingId,
  retryingId,
  onEdit,
  onToggle,
  onRetrySync,
  onDelete,
}: AddOnActionsProps) {
  const showRetry = !addOn.stripePriceId;
  const showDelete = !addOn.stripePriceId;
  const retryEnabled = canRetrySync(addOn);

  return (
    <>
      <button
        onClick={() => onEdit(addOn)}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        title="Editar add-on"
      >
        <Edit2 size={16} />
      </button>
      <button
        onClick={() => onToggle(addOn)}
        disabled={togglingId === addOn.id}
        className={`p-1.5 rounded ${
          addOn.isActive
            ? "text-green-600 hover:text-green-700 hover:bg-green-50"
            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        }`}
        title={
          addOn.isActive
            ? "Activo — clic para desactivar"
            : "Inactivo — clic para activar"
        }
      >
        {togglingId === addOn.id ? (
          <Loader2 size={16} className="animate-spin" />
        ) : addOn.isActive ? (
          <Eye size={16} />
        ) : (
          <EyeOff size={16} />
        )}
      </button>
      {showRetry && (
        <button
          onClick={() => onRetrySync(addOn)}
          disabled={retryingId === addOn.id || !retryEnabled}
          className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
          title={
            retryEnabled
              ? "Reintentar sincronización con Stripe"
              : "Actívalo y ponle un precio mayor que 0€ para poder sincronizar"
          }
        >
          {retryingId === addOn.id ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
        </button>
      )}
      {showDelete && (
        <button
          onClick={() => onDelete(addOn)}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          title="Eliminar add-on"
        >
          <Trash2 size={16} />
        </button>
      )}
    </>
  );
}

function AddOnCard(props: AddOnActionsProps) {
  const { addOn } = props;
  const featureLabel = planFeatureLabel(addOn.requiredPlanFeature);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Package className="h-5 w-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 truncate">
              {addOn.name}
            </div>
            <code className="text-xs text-gray-500">{addOn.code}</code>
          </div>
        </div>
        <span
          className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${addOn.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
        >
          {addOn.isActive ? "Activo" : "Inactivo"}
        </span>
      </div>

      <div className="mb-3">
        <span className="text-2xl font-bold text-gray-900">
          {addOn.price}€
        </span>
        <span className="text-gray-500">
          {" "}
          /{addOn.interval === "monthly" ? "mes" : "año"}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <StripeSyncBadge addOn={addOn} />
        {featureLabel && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 w-fit">
            Requiere: {featureLabel}
          </span>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-3">
        {addOn.activeSubscriptions} organización
        {addOn.activeSubscriptions !== 1 ? "es" : ""} con este add-on activo
      </p>

      <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
        <AddOnActionButtons {...props} />
      </div>
    </div>
  );
}

function AddOnRow(props: AddOnActionsProps) {
  const { addOn } = props;
  const featureLabel = planFeatureLabel(addOn.requiredPlanFeature);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="h-4 w-4 text-purple-600" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {addOn.name}
            </div>
            <code className="text-xs text-gray-500">{addOn.code}</code>
            {featureLabel && (
              <p className="text-xs text-blue-600 mt-0.5">
                Requiere: {featureLabel}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="py-4 px-4 whitespace-nowrap text-gray-900">
        {addOn.price}€{" "}
        <span className="text-gray-500">
          /{addOn.interval === "monthly" ? "mes" : "año"}
        </span>
      </td>
      <td className="py-4 px-4">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${addOn.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
        >
          {addOn.isActive ? "Activo" : "Inactivo"}
        </span>
      </td>
      <td className="py-4 px-4">
        <StripeSyncBadge addOn={addOn} />
      </td>
      <td className="py-4 px-4 text-gray-500">
        {addOn.activeSubscriptions} organización
        {addOn.activeSubscriptions !== 1 ? "es" : ""}
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center justify-end gap-1">
          <AddOnActionButtons {...props} />
        </div>
      </td>
    </tr>
  );
}

interface AddOnFormModalProps {
  editingAddOn: AddOn | null;
  formData: AddOnFormData;
  setFormData: React.Dispatch<React.SetStateAction<AddOnFormData>>;
  onPriceChange: (raw: string) => void;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

function AddOnFormModal({
  editingAddOn,
  formData,
  setFormData,
  onPriceChange,
  isSubmitting,
  onSubmit,
  onClose,
}: AddOnFormModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90dvh] flex flex-col">
        <h2 className="text-xl font-semibold text-gray-900 px-6 py-4 border-b border-gray-100 shrink-0">
          {editingAddOn ? "Editar add-on" : "Nuevo add-on"}
        </h2>

        <form onSubmit={onSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 min-h-0 space-y-4 overflow-y-auto px-6 py-4">
            {!editingAddOn ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, code: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder="Ej: voice_ai"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Identificador técnico, no se puede cambiar.
                </p>
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                Código:{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                  {editingAddOn.code}
                </code>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ej: Centralita IA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                placeholder="Descripción del add-on..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio (€)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  value={formData.price}
                  onChange={(e) => onPriceChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intervalo
                </label>
                <select
                  value={formData.interval}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      interval: e.target.value as "monthly" | "yearly",
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="monthly">Mensual</option>
                  <option value="yearly">Anual</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requiere función del plan (opcional)
              </label>
              <select
                value={formData.requiredPlanFeature}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    requiredPlanFeature: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Ninguna</option>
                {PLAN_FEATURE_OPTIONS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Si el plan actual de la organización no incluye esta función,
                el add-on se puede contratar igualmente pero no funcionará
                por completo.
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="addOnIsActive"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor="addOnIsActive"
                className="ml-2 text-sm text-gray-700"
              >
                Add-on activo (disponible para contratar)
              </label>
            </div>
          </div>

          <div className="flex space-x-3 px-6 py-4 border-t border-gray-100 shrink-0">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                  Guardando...
                </>
              ) : editingAddOn ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
